import {useState} from 'react';
import {Check, Circle, CircleDashed, Mail, Pencil, Phone, Play, Plus} from 'lucide-react';
import {Button} from '@common-web/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@common-web/components/ui/dropdown-menu';
import {cn} from '@common-web/lib/utils';
import {
  comingSoon,
  formatDate,
  type GroupDetail,
} from '@internal/pages/group-pipeline/components/pipelineDisplay';
import {
  menuActions,
  rowActions,
  type PipelineTask,
  type TaskAction,
} from '@internal/pages/group-pipeline/detail/taskActions';
import {NOT_BUILT, PreviewDot} from '@internal/pages/group-pipeline/components/PreviewDot';
import {AddTaskDialog} from '@internal/pages/group-pipeline/detail/AddTaskDialog';

export function GroupTasksTab({
  group,
  canManage,
  isPending,
  onAction,
  onStartPipeline,
}: {
  group: GroupDetail;
  onStartPipeline: () => void;
  canManage: boolean;
  isPending: boolean;
  onAction: (task: PipelineTask, action: TaskAction) => void;
}) {
  const pipeline = group.pipeline;
  const [addOpen, setAddOpen] = useState(false);

  if (!pipeline) {
    return (
      <div className='rounded-lg border bg-white px-6 py-16 text-center'>
        <p className='text-sm font-medium'>This group isn&apos;t in the pipeline.</p>
        <p className='mt-1 text-sm text-muted-foreground'>
          Start its pipeline to track onboarding tasks for it.
        </p>
        {canManage && (
          <Button className='mt-4' onClick={onStartPipeline}>
            <Play className='mr-1.5 h-4 w-4' />
            Add to pipeline
          </Button>
        )}
      </div>
    );
  }

  const sections = [...new Set(pipeline.tasks.map((t) => t.section))];
  // Held or lost groups take no task actions until they're resumed.
  const canAct = canManage && !pipeline.offState;

  return (
    <div className='grid items-start gap-4 xl:grid-cols-[1fr_300px]'>
      <div className='space-y-3'>
        {sections.map((section) => {
          const tasks = pipeline.tasks.filter((t) => t.section === section);
          const done = tasks.filter((t) => t.status === 'COMPLETED').length;
          return (
            <div key={section} className='overflow-hidden rounded-lg border bg-white'>
              <div className='flex items-center gap-2 border-b bg-gray-50 px-4 py-2.5'>
                <span className='text-xs font-bold uppercase tracking-[0.07em]'>{section}</span>
                <span className='text-sm text-gray-500'>
                  {done} of {tasks.length} complete
                </span>
              </div>
              <ul>
                {tasks.map((task) => (
                  <TaskRow
                    key={task.id}
                    task={task}
                    isOverdue={pipeline.state.overdueTaskIds.includes(task.id)}
                    canAct={canAct}
                    isPending={isPending}
                    onAction={onAction}
                  />
                ))}
              </ul>
            </div>
          );
        })}

        {canAct && (
          <Button variant='outline' className='w-full bg-white' onClick={() => setAddOpen(true)}>
            <Plus className='mr-1.5 h-4 w-4' />
            Add task
          </Button>
        )}
        <AddTaskDialog
          vbaGroupId={group.vbaGroupId}
          pipeline={pipeline}
          open={addOpen}
          onOpenChange={setAddOpen}
        />
      </div>

      <div className='space-y-3'>
        <div className='rounded-lg border bg-white p-4'>
          <div className='mb-3 flex items-center justify-between'>
            <span className='text-sm font-semibold'>Go-live readiness</span>
            <span className='text-sm text-gray-500'>{pipeline.state.readiness}% complete</span>
          </div>
          <div className='h-2 overflow-hidden rounded-full bg-gray-200'>
            <div
              className='h-full rounded-full bg-gray-900 transition-all'
              style={{width: `${pipeline.state.readiness}%`}}
            />
          </div>
        </div>

        <div className='space-y-3 rounded-lg border bg-white p-4'>
          <span className='text-sm font-semibold'>Contacts</span>
          <div className='space-y-1 text-sm'>
            <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>Employer</p>
            <p className='font-medium'>{group.primaryContact.name ?? 'Not on file'}</p>
            {pipeline.primaryContactTitle && (
              <p className='text-gray-500'>{pipeline.primaryContactTitle}</p>
            )}
            {group.primaryContact.email && (
              <p className='flex items-start gap-1.5 text-gray-600 [overflow-wrap:anywhere]'>
                <Mail className='mt-0.5 h-3.5 w-3.5 shrink-0' />
                {group.primaryContact.email}
              </p>
            )}
            {group.primaryContact.phone && (
              <p className='flex items-center gap-1.5 text-gray-600'>
                <Phone className='h-3.5 w-3.5' />
                {group.primaryContact.phone}
              </p>
            )}
          </div>
          <div className='flex gap-2'>
            <Button
              variant='outline'
              size='sm'
              className='relative bg-white'
              title={NOT_BUILT}
              onClick={comingSoon('Log call')}
            >
              <Phone className='mr-1 h-3.5 w-3.5' />
              Log call
              <PreviewDot />
            </Button>
            <Button
              variant='outline'
              size='sm'
              className='relative bg-white'
              title={NOT_BUILT}
              onClick={comingSoon('Log email')}
            >
              <Mail className='mr-1 h-3.5 w-3.5' />
              Log email
              <PreviewDot />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function TaskRow({
  task,
  isOverdue,
  canAct,
  isPending,
  onAction,
}: {
  task: PipelineTask;
  isOverdue: boolean;
  canAct: boolean;
  isPending: boolean;
  onAction: (task: PipelineTask, action: TaskAction) => void;
}) {
  const detail =
    task.status === 'COMPLETED'
      ? `Completed ${formatDate(task.completedAt)}`
      : task.status === 'SKIPPED'
        ? `Skipped: ${task.skipReason ?? 'no reason given'}`
        : task.status === 'OPEN' && task.dueAt
          ? `Due ${formatDate(task.dueAt)}`
          : null;

  return (
    <li className='flex items-center gap-3 border-b px-4 py-3 last:border-0'>
      <StatusIcon status={task.status} />
      <div className='min-w-0 flex-1'>
        <div className='flex flex-wrap items-center gap-2'>
          <span
            className={cn('text-sm font-medium', task.status === 'NOT_STARTED' && 'text-gray-500')}
          >
            {task.label}
          </span>
          {task.attemptCount > 1 && (
            <span className='text-xs text-gray-500'>Attempt {task.attemptCount}</span>
          )}
          {isOverdue && (
            <span className='rounded-md bg-red-50 px-1.5 py-0.5 text-xs font-semibold text-red-700'>
              Overdue
            </span>
          )}
          {!task.definitionId && (
            <span className='rounded-md bg-blue-50 px-1.5 py-0.5 text-xs font-semibold text-blue-700'>
              One-off
            </span>
          )}
        </div>
        {detail && <p className='text-sm text-gray-500'>{detail}</p>}
      </div>
      {canAct &&
        rowActions(task).map((action) => (
          <Button
            key={action.key}
            variant='outline'
            size='sm'
            className='relative bg-white'
            title={action.placeholder ? NOT_BUILT : undefined}
            disabled={isPending}
            onClick={() => onAction(task, action)}
          >
            {action.key === 'complete' && <Check className='mr-1 h-3.5 w-3.5' />}
            {action.label}
            {action.placeholder && <PreviewDot />}
          </Button>
        ))}
      {canAct && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant='outline'
              size='icon'
              className='h-8 w-8 bg-white'
              aria-label={`Edit ${task.label}`}
              disabled={isPending}
            >
              <Pencil className='h-3.5 w-3.5' />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end'>
            {menuActions(task).map((action) => (
              <DropdownMenuItem
                key={action.key}
                title={action.placeholder ? NOT_BUILT : undefined}
                onSelect={() => onAction(task, action)}
              >
                {action.label}
                {action.placeholder && <PreviewDot inline />}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </li>
  );
}

function StatusIcon({status}: {status: PipelineTask['status']}) {
  if (status === 'COMPLETED') {
    return (
      <span className='flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-green-600'>
        <Check className='h-3 w-3 text-white' strokeWidth={3} />
      </span>
    );
  }
  if (status === 'SKIPPED') return <CircleDashed className='h-5 w-5 shrink-0 text-gray-400' />;
  if (status === 'OPEN') return <Circle className='h-5 w-5 shrink-0 text-orange-500' />;
  return <Circle className='h-5 w-5 shrink-0 fill-gray-100 text-gray-300' />;
}
